import { useEffect, useRef, useState } from "react";
import SectionHeader from "../SectionHeader";
import ActionAlert from "../ActionAlert";
import DrawHistoryTable, { Draw } from "../DrawHistoryTable";
import { useNavigate } from "react-router-dom";
import { getDraws } from "../../../../../services/drawService";

interface Lender {
  companyName?: string;
}

interface DrawsSectionProps {
  isActive: boolean;
  projectId: string;
  lender?: Lender;
  projectStatus: string;
}

interface ApiResponse {
  data?: Draw[] | { data?: Draw[] };
}

const PENDING_STATUSES = [
  "submitted",
  "inspection_pending",
  "owner_signature_pending",
];

const formatAmount = (amountObj?: Draw["requestedAmount"]): string => {
  if (!amountObj) return "";
  const symbol = amountObj.currency?.symbol ?? "$";
  return `${symbol}${Number(amountObj.amount ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
};

const formatDate = (val?: string): string => {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d.getTime())
    ? val
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const DrawsSection = ({
  isActive,
  projectId,
  lender,
  projectStatus,
}: DrawsSectionProps) => {
  const navigate = useNavigate();
  const [draws, setDraws] = useState<Draw[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  const fetchDraws = () => {
    if (!projectId) return;
    setLoading(true);
    (getDraws(projectId) as Promise<ApiResponse>).then(({ data }) => {
      const list: Draw[] = Array.isArray((data as { data?: Draw[] })?.data)
        ? (data as { data: Draw[] }).data
        : Array.isArray(data)
          ? (data as Draw[])
          : [];
      setDraws(list);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (!isActive || !projectId || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchDraws();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isActive]);

  const pendingDraw =
    draws
      .filter((d) => PENDING_STATUSES.includes(d.status ?? ""))
      .sort((a, b) => (b.drawNumber ?? 0) - (a.drawNumber ?? 0))[0] ?? null;

  const alertTitle = pendingDraw
    ? `Draw #${String(pendingDraw.drawNumber).padStart(2, "0")} Submitted — Awaiting CSC Inspection`
    : null;

  const alertText = pendingDraw
    ? [
        formatAmount(pendingDraw.requestedAmount),
        (pendingDraw as Draw & { drawDate?: string }).drawDate
          ? `Submitted ${formatDate((pendingDraw as Draw & { drawDate?: string }).drawDate)}`
          : null,
        pendingDraw.ownerSignatureUrl
          ? "Owner signed"
          : "Owner signature pending",
        "Inspection pending",
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className={`section ${isActive ? "active" : ""}`} id="sec-draws">
      <SectionHeader
        title="Draw Requests"
        subtitle="Submit and track AIA Application for Payment draws. Each draw triggers a CSC field inspection before funds are released."
      />

      {!loading && pendingDraw && (
        <ActionAlert
          icon="⏳"
          title={alertTitle ?? ""}
          text={alertText ?? ""}
        />
      )}

      <DrawHistoryTable
        draws={draws}
        loading={loading}
        projectId={projectId}
        onSubmit={() => navigate(`/draw-request/${projectId}`)}
        projectStatus={projectStatus}
        onCancelled={fetchDraws}
      />

      <div className="tableNote">
        <strong>Draw requirements reminder:</strong> Each draw must include AIA
        G702 + G703, subcontractor invoices, lien waiver documentation, and must
        be signed by the Owner/Borrower. A CSC field inspection is required
        before {lender?.companyName || "the bank"} will release funds.
      </div>
    </div>
  );
};

export default DrawsSection;
